/**
 * locale-link-guard.js — keep a Chinese page's links in Chinese.
 *
 * Shopify decides the routes root, not the theme. For a navigation that arrives from another
 * site (Google, ads, email: `Sec-Fetch-Site: cross-site`), Shopify renders the Chinese page
 * (lang="zh-TW", Chinese copy) with its routes root set to "/en/", and its page cache then serves
 * that render to later visitors too. Every link the theme builds from routes (logo, menus, cart
 * drawer, search, cart) then points into /en/, and the first click silently switches the
 * visitor to English (found 2026-09-28; see test/locale-link-guard.test.js).
 *
 * The guard enforces the invariant on Chinese pages only: nothing sends the visitor, or a cart
 * or search request, into /en/, unless the link opts in with `data-locale-switch`. English pages
 * are left alone. Loaded synchronously right after `window.routes` in layout/theme.liquid.
 */
(function(root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else if (root && root.document) {
    api.install(root, root.document);
  }
})(typeof window !== 'undefined' ? window : this, function() {
  'use strict';

  var ENGLISH_PREFIX = /^(\/en)(?=\/|\?|#|$)/;
  var SWITCH_ATTR = 'data-locale-switch';

  function isEnglishLang(lang) {
    var code = String(lang || '').toLowerCase();
    return code === 'en' || code.indexOf('en-') === 0;
  }

  /** The same URL without its /en locale prefix, or null when it has none or is another site's. */
  function stripEnglishPrefix(url, origin) {
    if (typeof url !== 'string' || !url) return null;
    var host = String(origin || '').replace(/^https?:/, '');   // "//inutritionhk.com"
    var lead = '';
    var rest = url;
    if (/^https?:\/\//i.test(url) || url.indexOf('//') === 0) {
      var scheme = url.indexOf('//') === 0 ? '' : url.slice(0, url.indexOf('//'));
      if (!host || url.slice(scheme.length).indexOf(host) !== 0) return null;
      var afterHost = url.slice(scheme.length + host.length);
      if (afterHost && '/?#'.indexOf(afterHost.charAt(0)) === -1) return null;   // e.g. inutritionhk.com.evil
      lead = url.slice(0, scheme.length + host.length);
      rest = afterHost;
    } else if (url.charAt(0) !== '/') {
      return null;
    }
    if (!ENGLISH_PREFIX.test(rest)) return null;
    var stripped = rest.replace(ENGLISH_PREFIX, '');
    if (stripped === '' || stripped.charAt(0) === '?' || stripped.charAt(0) === '#') stripped = '/' + stripped;
    return lead + stripped;
  }

  function fixAttribute(el, attr, origin) {
    if (!el || !el.getAttribute || el.hasAttribute(SWITCH_ATTR)) return;
    var fixed = stripEnglishPrefix(el.getAttribute(attr), origin);
    if (fixed !== null) el.setAttribute(attr, fixed);
  }

  function fixElement(el, origin) {
    if (!el || el.nodeType !== 1) return;
    var tag = el.tagName;
    if (tag === 'A' || tag === 'AREA') fixAttribute(el, 'href', origin);
    else if (tag === 'FORM') fixAttribute(el, 'action', origin);
  }

  function fixTree(node, origin) {
    if (!node) return;
    fixElement(node, origin);
    if (!node.querySelectorAll) return;
    var found = node.querySelectorAll('a[href], area[href], form[action]');
    for (var i = 0; i < found.length; i++) fixElement(found[i], origin);
  }

  function fixRoutes(window, origin) {
    var routes = window.routes;
    if (routes && typeof routes === 'object') {
      Object.keys(routes).forEach(function(key) {
        var fixed = stripEnglishPrefix(routes[key], origin);
        if (fixed !== null) routes[key] = fixed;
      });
    }
    var shopifyRoutes = window.Shopify && window.Shopify.routes;
    if (shopifyRoutes) {
      var root = stripEnglishPrefix(shopifyRoutes.root, origin);
      if (root !== null) shopifyRoutes.root = root;
    }
  }

  function install(window, document) {
    if (!window || !document || !document.documentElement) return;
    if (isEnglishLang(document.documentElement.getAttribute('lang'))) return;
    var origin = (window.location && window.location.origin) || '';

    fixRoutes(window, origin);
    fixTree(document.documentElement, origin);

    if (typeof window.MutationObserver === 'function') {
      new window.MutationObserver(function(mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          if (m.type === 'attributes') fixElement(m.target, origin);
          else for (var j = 0; j < m.addedNodes.length; j++) fixTree(m.addedNodes[j], origin);
        }
      }).observe(document.documentElement, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['href', 'action'],
      });
    }

    // Last line of defense: fix the target at the moment of use.
    document.addEventListener('click', function(event) {
      var el = event.target && event.target.closest ? event.target.closest('a[href], area[href]') : null;
      fixElement(el, origin);
    }, true);
    document.addEventListener('submit', function(event) {
      fixElement(event.target, origin);
    }, true);
    document.addEventListener('DOMContentLoaded', function() {
      fixRoutes(window, origin);
      fixTree(document.documentElement, origin);
    });
  }

  return { install: install, stripEnglishPrefix: stripEnglishPrefix, isEnglishLang: isEnglishLang };
});
